import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import GoogleMapPicker from './GoogleMapPicker';
import MapboxPicker from './MapboxPicker';

interface MapPickerProps {
  onLocationSelect: (location: MapLocation) => void;
  initialLocation?: Coordinates;
  initialAddress?: Address;
}

interface MapLocation {
  coordinates: Coordinates;
  address?: Address;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface Address {
  street: string;
  ward: string;
  city: string;
  state: string;
  country: string;
  postalCode?: string;
}

interface MapSettings {
  platform: 'google' | 'mapbox';
  apiKey: string;
}

const MapPicker: React.FC<MapPickerProps> = ({
  onLocationSelect,
  initialLocation,
  initialAddress
}) => {
  const { t } = useTranslation();
  const [mapSettings, setMapSettings] = useState<MapSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMapSettings = async () => {
      try {
        const response = await api.get('/settings/maps');
        setMapSettings(response.data);
      } catch (error) {
        console.error('Error fetching map settings:', error);
        setError('Error loading map settings');
      } finally {
        setLoading(false);
      }
    };

    fetchMapSettings();
  }, []);

  if (loading) {
    return <div className="h-96 flex items-center justify-center">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="h-96 flex items-center justify-center text-red-500">{error}</div>;
  }

  if (!mapSettings) {
    return <div className="h-96 flex items-center justify-center text-red-500">No map settings available</div>;
  }

  if (mapSettings.platform === 'google') {
    return (
      <GoogleMapPicker
        apiKey={mapSettings.apiKey}
        onLocationSelect={onLocationSelect}
        initialLocation={initialLocation}
        initialAddress={initialAddress}
      />
    );
  }

  return (
    <MapboxPicker
      apiKey={mapSettings.apiKey}
      onLocationSelect={onLocationSelect}
      initialLocation={initialLocation}
      initialAddress={initialAddress}
    />
  );
};

export default MapPicker; 